import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Copy, Download, ExternalLink, Printer, QrCode, RefreshCw, Plus } from 'lucide-react';

const DEFAULT_FORM = {
  type: 'pay me',
  title: '',
  target_url: '',
};

const QR_TYPES = [
  'pay me',
  'store',
  'product',
  'invoice',
  'donation/payment link',
  'vcard',
  'whatsapp order',
];

function typeLabel(type) {
  switch (type) {
    case 'donation/payment link':
      return 'Donation / Payment Link';
    case 'pay me':
      return 'Pay Me';
    case 'whatsapp order':
      return 'WhatsApp Order';
    default:
      return String(type || '').replace(/\b\w/g, (m) => m.toUpperCase());
  }
}

export default function QRCodesPage() {
  const [data, setData] = useState({ qr_codes: [], access: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [copied, setCopied] = useState('');

  const canUseQr = !!data?.access?.can_use_qr_codes;

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/qr-codes');
      setData(res.data || { qr_codes: [], access: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load QR codes');
      setData({ qr_codes: [], access: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    if (!canUseQr) {
      toast.error('Upgrade your package to generate QR codes.');
      return;
    }
    if (!form.target_url.trim()) {
      toast.error('Target URL is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title,
        target_url: form.target_url,
      };
      const res = await api.post('/dashboard/qr-codes', payload);
      toast.success(res.data?.message || 'QR code generated');
      setForm(DEFAULT_FORM);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate QR code');
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success('Copied');
      setTimeout(() => setCopied(''), 1200);
    } catch {
      toast.error('Could not copy');
    }
  };

  const downloadQr = (qr) => {
    const href = qr.image_url || qr.generated_data;
    if (!href) return;
    const link = document.createElement('a');
    link.href = href;
    link.download = `${(qr.title || 'qr-code').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printPage = () => window.print();

  const codes = useMemo(() => data.qr_codes || [], [data.qr_codes]);

  if (loading) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>QR Codes</h1>
          <p className="text-slate-500 text-sm mt-0.5">Generate printable QR codes for payments, stores, invoices and public links.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="border-slate-200" onClick={load}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={printPage}>
            <Printer size={14} className="mr-1" /> Print
          </Button>
        </div>
      </div>

      {!canUseQr ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Upgrade your package to enable QR code generation.
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Plus size={18} /> Create QR Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => update('type', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QR_TYPES.map((opt) => <SelectItem key={opt} value={opt}>{typeLabel(opt)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => update('title', e.target.value)} className="mt-1" placeholder="Pay Me QR" />
              </div>
              <div>
                <Label>Target URL</Label>
                <Textarea
                  value={form.target_url}
                  onChange={(e) => update('target_url', e.target.value)}
                  className="mt-1 min-h-[110px]"
                  placeholder="https://pay.jjuma.com/pay/your-link"
                />
              </div>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white w-full" disabled={saving}>
                <QrCode size={16} className="mr-1" /> {saving ? 'Generating...' : 'Generate QR'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Saved QR Codes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {codes.length ? codes.map((qr) => (
              <div key={qr.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4 grid gap-4 sm:grid-cols-[140px_1fr]">
                <div className="rounded-2xl bg-white border border-slate-100 p-3 flex items-center justify-center">
                  {qr.image_url ? <img src={qr.image_url} alt={qr.title} className="w-full max-w-[120px]" /> : null}
                </div>
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{qr.title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className="bg-emerald-100 text-emerald-700">{typeLabel(qr.type)}</Badge>
                        <span className="text-xs text-slate-500 break-all">{qr.target_url}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" className="border-slate-200" onClick={() => copyText(qr.target_url, qr.id)}>
                      <Copy size={14} className="mr-1" /> {copied === qr.id ? 'Copied' : 'Copy URL'}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="border-slate-200" onClick={() => downloadQr(qr)}>
                      <Download size={14} className="mr-1" /> Download
                    </Button>
                    <a href={qr.target_url} target="_blank" rel="noreferrer" className="inline-flex">
                      <Button type="button" size="sm" variant="outline" className="border-slate-200">
                        <ExternalLink size={14} className="mr-1" /> Open
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-sm text-slate-500">No QR codes generated yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
